import SearchArea from "component/admin/search/searchArea";
import style from "css/admin/component.module.css";
import { useCallback, useEffect, useMemo, useState } from "react";

import { Button, ConfigProvider, Modal, Table, Tag, Tooltip, message } from "antd";
import { EditOutlined } from "@ant-design/icons";
import { ReactComponent as ClearAllIcon } from "asset/clear_all_selected.svg";
import { ReactComponent as ConfirmAllIcon } from "asset/confirm_all_selected.svg";
import { ReactComponent as PlusIcon } from "asset/plus_icon.svg";
import { ReactComponent as UploadIcon } from "asset/upload_icon.svg";
import axios from "axios";
import SearchButton from "component/admin/search/searchButton";
import SearchDateRange from "component/admin/search/searchDateRange";
import SearchDropdown from "component/admin/search/searchDropdown";
import SearchInput from "component/admin/search/searchInput";
import SearchItem from "component/admin/search/searchItem";
import SearchRow from "component/admin/search/searchRow";
import ApproveModal from "./approveModal";
import ExcelUploadModal from "./excelUploadModal";
import ManualRegisterModal from "./manualRegisterModal";
import DeleteApplicationModal from "./deleteApplicationModal";
import ResultModal from "./resultModal";

function Application() {
  const [init, setInit] = useState(true);

  // 검색 필터
  const [searchPeriod, setSearchPeriod] = useState("ALL");
  const [searchStatus, setSearchStatus] = useState("ALL");
  const [searchLicense, setSearchLicense] = useState("ALL");
  const [searchDepartment, setSearchDepartment] = useState("ALL");
  const [searchRole, setSearchRole] = useState("ALL");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [searchText, setSearchText] = useState("");

  // Select box 옵션
  const [periodList, setPeriodList] = useState([]);
  const [statusList, setStatusList] = useState([]);
  const [departmentList, setDepartmentList] = useState([]);
  const [licenseList, setLicenseList] = useState([]);
  const [roleList, setRoleList] = useState([]);

  // Select box 옵션 로딩
  const [periodLoading, setPeriodLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [departmentLoading, setDepartmentLoading] = useState(false);
  const [licenseLoading, setLicenseLoading] = useState(false);
  const [roleLoading, setRoleLoading] = useState(false);

  // 조회된 결과 리스트
  const [dataList, setDataList] = useState([]);
  const [totalCnt, setTotalCnt] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [dataLoading, setDataLoading] = useState(false);
  const pageSize = 10;
  // 선택된 데이터 리스트
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);

  // 승인 모달
  const [approveOpen, setApproveOpen] = useState(false);
  const [approveTaskType, setApproveTaskType] = useState("approve");
  const [approveNotice, setApproveNotice] = useState("");


  // 삭제 모달
  const [deleteOpen, setDeleteOpen] = useState(false);

  // 수기등록 / 엑셀 업로드 모달
  const [manualOpen, setManualOpen] = useState(false);
  const [excelUploadOpen, setExcelUploadOpen] = useState(false);

  // 이메일 수정 모달
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [editEmailAppId, setEditEmailAppId] = useState(null);
  const [editEmailValue, setEditEmailValue] = useState("");

  // 엑셀 다운로드 로딩
  const [downloadLoading, setDownloadLoading] = useState(false);

  // 작업 결과 모달
  const [resultOpen, setResultOpen] = useState(false);
  const [resultTitle, setResultTitle] = useState("");
  const [resultDescription, setResultDescription] = useState("");
  const [resultSummary, setResultSummary] = useState({
    total: 0,
    success: 0,
    fail: 0,
  });
  const [resultRows, setResultRows] = useState([]);

  const buildResultRows = useCallback((rows, apiResult) => {
    const successIds = Array.isArray(apiResult?.successIds)
      ? apiResult.successIds
      : [];
    const failIds = Array.isArray(apiResult?.failIds) ? apiResult.failIds : [];
    const hasPerItemResult = successIds.length > 0 || failIds.length > 0;

    const successSet = new Set(successIds.map((v) => String(v)));
    const failSet = new Set(failIds.map((v) => String(v)));
    const messageFallback = apiResult?.message ? String(apiResult.message) : "";
    const updatedCntRaw = apiResult?.updatedCnt;
    const updatedCnt =
      typeof updatedCntRaw === "number" ? updatedCntRaw : Number(updatedCntRaw);

    return (rows || []).map((r) => {
      const appId = r?.APPLICATION_ID != null ? String(r.APPLICATION_ID) : "";
      const isSuccess = appId && successSet.has(appId);
      const isFail = appId && failSet.has(appId);

      // 서버가 successIds/failIds를 안 내려주는 경우(구버전 extend 응답 등) fallback:
      // - updatedCnt > 0 이면 "성공"으로 표시 (대부분 IN 업데이트는 대상이 존재하면 일괄 성공)
      // - updatedCnt === 0 이면 "실패"로 표시
      const useFallbackByUpdatedCnt =
        !hasPerItemResult && Number.isFinite(updatedCnt);
      const fallbackStatus = useFallbackByUpdatedCnt
        ? updatedCnt > 0
          ? "success"
          : "fail"
        : null;

      const status = isSuccess
        ? "success"
        : isFail
          ? "fail"
          : fallbackStatus || "fail";

      const resultMsg = isFail
        ? messageFallback || ""
        : status === "success"
          ? ""
          : useFallbackByUpdatedCnt
            ? messageFallback || `업데이트 0건`
            : "결과 누락";
      return {
        APPLICATION_ID: r?.APPLICATION_ID,
        MEMBER_ID: r?.MEMBER_ID,
        MEMBER_NM: r?.MEMBER_NM,
        DEPARTMENT_NM: r?.DEPARTMENT_NM,
        LICENSE_NM: [r?.MANUFACTURER_NM, r?.PRODUCT_NM]
          .filter(Boolean)
          .join(" "),
        RESULT_STATUS: status,
        RESULT_MESSAGE: resultMsg,
      };
    });
  }, []);

  const openResultWith = useCallback(
    (title, description, rows, apiResult) => {
      const built = buildResultRows(rows, apiResult);
      const successCnt = built.filter(
        (r) => r.RESULT_STATUS === "success",
      ).length;
      const failCnt = built.filter((r) => r.RESULT_STATUS === "fail").length;

      setResultTitle(title);
      setResultDescription(description || "");
      setResultRows(built);
      setResultSummary({
        total: built.length,
        success: successCnt,
        fail: failCnt,
      });
      setResultOpen(true);
    },
    [buildResultRows],
  );

  const getApplicantList = useCallback(
    (page = 1) => {
      setDataLoading(true);
      const start =
        startDate && typeof startDate.format === "function"
          ? startDate.format("YYYY-MM-DD")
          : null;
      const end =
        endDate && typeof endDate.format === "function"
          ? endDate.format("YYYY-MM-DD")
          : null;
      const config = {
        params: {
          SEARCH_PERIOD: searchPeriod,
          SEARCH_STATUS: searchStatus,
          SEARCH_LICENSE: searchLicense,
          SEARCH_DEPARTMENT: searchDepartment,
          SEARCH_ROLE: searchRole,
          START_DATE: start,
          END_DATE: end,
          SEARCH_TEXT: searchText,
          page: page,
          size: pageSize,
        },
      };
      axios
        .get("/application", config)
        .then((res) => {
          if (res.status === 200) {
            setDataList(res.data.list || []);
            setTotalCnt(res.data.totalCnt || 0);
            setCurrentPage(res.data.page || 1);
          }
        })
        .catch((err) => {
          console.log(err);
        })
        .finally(() => setDataLoading(false));
    },
    [
      endDate,
      searchDepartment,
      searchLicense,
      searchPeriod,
      searchRole,
      searchStatus,
      searchText,
      startDate,
    ],
  );

  const getStatusMeta = useCallback(
    (statusCode) => {
      if (statusCode == null) return { label: "-", code: "", matched: true };
      const code = String(statusCode).trim();
      if (!code) return { label: "-", code: "", matched: true };

      const matched = statusList.find((it) => String(it?.key) === code);
      if (matched?.label) return { label: matched.label, code, matched: true };

      return { label: "알수없음", code, matched: false };
    },
    [statusList],
  );

  const attachStatusLabel = useCallback(
    (rows) => {
      return (rows || []).map((r) => ({
        ...r,
        STATUS_LABEL: getStatusMeta(r?.STATUS).label,
      }));
    },
    [getStatusMeta],
  );

  const getLicenseMeta = useCallback(
    (productCd, record) => {
      if (productCd == null) return { label: "-", code: "", matched: true };
      const code = String(productCd).trim();
      if (!code) return { label: "-", code: "", matched: true };

      const matched = licenseList.find((it) => String(it?.key) === code);
      if (matched?.label) return { label: matched.label, code, matched: true };

      const manu = record?.MANUFACTURER_NM
        ? String(record.MANUFACTURER_NM).trim()
        : "";
      const prod = record?.PRODUCT_NM ? String(record.PRODUCT_NM).trim() : "";
      const fallbackLabel = [manu, prod].filter(Boolean).join(" ");
      if (fallbackLabel) return { label: fallbackLabel, code, matched: true };

      return { label: "알수없음", code, matched: false };
    },
    [licenseList],
  );

  const getRoleMeta = useCallback(
    (typeCode) => {
      if (typeCode == null) return { label: "-", code: "", matched: true };
      const code = String(typeCode).trim();
      if (!code) return { label: "-", code: "", matched: true };

      const matched = roleList.find((it) => String(it?.key) === code);
      if (matched?.label) return { label: matched.label, code, matched: true };

      return { label: code, code, matched: false };
    },
    [roleList],
  );

  // 신청기간 dropdown 옵션 조회
  const getPeriodList = useCallback(() => {
    setPeriodLoading(true);
    axios
      .get("/period")
      .then((res) => {
        if (res.status === 200 && Array.isArray(res.data)) {
          const items = [
            { key: "ALL", label: "전체" },
            ...res.data.map((row) => ({
              key: row?.PERIOD_ID,
              label: row?.PERIOD_NM,
            })),
          ];
          setPeriodList(items);
        } else {
          setPeriodList([{ key: "ALL", label: "전체" }]);
        }
      })
      .catch((err) => {
        console.log(err);
        setPeriodList([{ key: "ALL", label: "전체" }]);
      })
      .finally(() => setPeriodLoading(false));
  }, []);

  // 상태 dropdown의 옵션 조회
  const getStatusList = useCallback(() => {
    setStatusLoading(true);
    const config = {
      params: {
        GROUP_CD: "CD02",
      },
    };
    axios
      .get("/code", config)
      .then((res) => {
        if (res.status === 200 && Array.isArray(res.data)) {
          const items = [
            { key: "ALL", label: "전체" },
            ...res.data.map((row) => ({
              key: row?.CODE_CD,
              label: row?.VIEW_NM,
            })),
          ];
          setStatusList(items);
        } else {
          setStatusList([{ key: "ALL", label: "전체" }]);
        }
      })
      .catch((err) => {
        console.log(err);
        setStatusList([{ key: "ALL", label: "전체" }]);
      })
      .finally(() => setStatusLoading(false));
  }, []);
  // 라이선스 dropdown의 옵션 조회
  const getLicenseList = useCallback(() => {
    setLicenseLoading(true);
    axios
      .get("/product/detail")
      .then((res) => {
        if (res.status === 200 && Array.isArray(res.data)) {
          const items = [
            { key: "ALL", label: "전체" },
            ...res.data.map((row) => ({
              key: row?.PRODUCT_CD,
              label: `${row?.MANUFACTURER_NM} ${row?.PRODUCT_NM}`,
            })),
          ];
          setLicenseList(items);
        } else {
          setLicenseList([{ key: "ALL", label: "전체" }]);
        }
      })
      .catch((err) => {
        console.log(err);
        setLicenseList([{ key: "ALL", label: "전체" }]);
      })
      .finally(() => setLicenseLoading(false));
  }, []);
  // 학과 dropdown의 옵션 조회
  const getDepartmentList = useCallback(() => {
    setDepartmentLoading(true);
    axios
      .get("/department")
      .then((res) => {
        if (res.status === 200 && Array.isArray(res.data)) {
          const items = [
            { key: "ALL", label: "전체" },
            ...res.data.map((row) => ({
              key: row?.DEPARTMENT_CD,
              label: row?.DEPARTMENT_NM,
            })),
          ];
          setDepartmentList(items);
        } else {
          setDepartmentList([{ key: "ALL", label: "전체" }]);
        }
      })
      .catch((err) => {
        console.log(err);
        setDepartmentList([{ key: "ALL", label: "전체" }]);
      })
      .finally(() => setDepartmentLoading(false));
  }, []);
  // 권한 dropdown의 옵션 조회
  const getRoleList = useCallback(() => {
    setRoleLoading(true);
    const config = {
      params: {
        GROUP_CD: "CD01",
      },
    };
    axios
      .get("/code", config)
      .then((res) => {
        if (res.status === 200 && Array.isArray(res.data)) {
          const items = [
            { key: "ALL", label: "전체" },
            ...res.data.map((row) => ({
              key: row?.CODE_CD,
              label: row?.VIEW_NM,
            })),
          ];
          setRoleList(items);
        } else {
          setRoleList([{ key: "ALL", label: "전체" }]);
        }
      })
      .catch((err) => {
        console.log(err);
        setRoleList([{ key: "ALL", label: "전체" }]);
      })
      .finally(() => setRoleLoading(false));
  }, []);

  useEffect(() => {
    if (init) {
      getPeriodList();
      getStatusList();
      getLicenseList();
      getDepartmentList();
      getRoleList();
      getApplicantList(1);
      setInit(false);
    }
  }, [
    getApplicantList,
    getDepartmentList,
    getLicenseList,
    getPeriodList,
    getRoleList,
    getStatusList,
    init,
  ]);

  const columns = useMemo(
    () => [
      {
        dataIndex: "checkbox",
        key: "checkbox",
        width: 48,
        render: () => null,
      },
      {
        title: "학번",
        dataIndex: "MEMBER_ID",
        key: "MEMBER_ID",
        width: 120,
        ellipsis: true,
        render: (text) => <span className={style.table_cell}>{text}</span>,
      },
      {
        title: "이름",
        dataIndex: "MEMBER_NM",
        key: "MEMBER_NM",
        width: 120,
        ellipsis: true,
        render: (text) => <span className={style.table_cell}>{text}</span>,
      },
      {
        title: "이메일",
        dataIndex: "EMAIL",
        key: "EMAIL",
        width: 220,
        ellipsis: true,
        render: (text) => (
          <span className={style.table_cell}>{text || "-"}</span>
        ),
      },
      {
        title: "신청 이메일",
        dataIndex: "APPLY_EMAIL",
        key: "APPLY_EMAIL",
        width: 220,
        ellipsis: true,
        render: (text, record) => (
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <span className={style.table_cell}>{text || "-"}</span>
            {(record?.STATUS === "CD0202" || record?.STATUS === "CD0203") && (
              <EditOutlined
                style={{ cursor: "pointer", color: "#527ff2", fontSize: 13 }}
                onClick={() => {
                  setEditEmailAppId(record?.APPLICATION_ID);
                  setEditEmailValue(text || "");
                  setEmailModalOpen(true);
                }}
              />
            )}
          </div>
        ),
      },
      {
        title: "학과",
        dataIndex: "DEPARTMENT_NM",
        key: "DEPARTMENT_NM",
        width: 160,
        ellipsis: true,
        render: (text) => <span className={style.table_cell}>{text}</span>,
      },
      {
        title: "구분",
        dataIndex: "TYPE",
        key: "TYPE",
        width: 70,
        ellipsis: true,
        render: (code) => {
          const meta = getRoleMeta(code);
          const labelNode = meta.matched ? (
            meta.label
          ) : (
            <span title={`미매칭 코드: ${meta.code}`}>{meta.label}</span>
          );
          return <span className={style.table_cell}>{labelNode}</span>;
        },
      },
      {
        title: "신청일시",
        dataIndex: "APPLY_DT",
        key: "APPLY_DT",
        width: 140,
        ellipsis: true,
        render: (text) => <span className={style.table_cell}>{text}</span>,
      },
      {
        title: "승인일시",
        dataIndex: "APPROVE_DT",
        key: "APPROVE_DT",
        width: 140,
        ellipsis: true,
        render: (text) => <span className={style.table_cell}>{text}</span>,
      },
      {
        title: "만료일시",
        dataIndex: "EXPIRE_DT",
        key: "EXPIRE_DT",
        width: 100,
        ellipsis: true,
        render: (text) => <span className={style.table_cell}>{text}</span>,
      },
      {
        title: "상태",
        dataIndex: "STATUS",
        key: "STATUS",
        width: 70,
        render: (code, record) => {
          const meta = getStatusMeta(code);
          const labelNode = meta.matched ? (
            meta.label
          ) : (
            <span title={`미매칭 코드: ${meta.code}`}>{meta.label}</span>
          );
          const errMsg = record?.ERROR_MESSAGE;

          let tag;
          // 만료
          if (code === "CD0205") {
            tag = (
              <Tag
                color="#fff"
                className={style.status_tag}
                style={{ backgroundColor: "#444", cursor: errMsg ? "pointer" : "default" }}
              >
                {labelNode}
              </Tag>
            );
            // 만료예정
          } else if (code === "CD0204") {
            tag = (
              <Tag
                color={"#595959"}
                className={style.status_tag}
                style={{ color: "#000", cursor: errMsg ? "pointer" : "default" }}
              >
                {labelNode}
              </Tag>
            );
            // 완료(사용중)
          } else if (code === "CD0201") {
            tag = (
              <Tag color="processing" className={style.status_tag} style={{ cursor: errMsg ? "pointer" : "default" }}>
                {labelNode}
              </Tag>
            );
            // 대기
          } else if (code === "CD0202") {
            tag = (
              <Tag color="warning" className={style.status_tag} style={{ cursor: errMsg ? "pointer" : "default" }}>
                {labelNode}
              </Tag>
            );
            // 에러
          } else if (code === "CD0203") {
            tag = (
              <Tag color="error" className={style.status_tag} style={{ cursor: errMsg ? "pointer" : "default" }}>
                {labelNode}
              </Tag>
            );
          } else {
            tag = (
              <Tag color="default" className={style.status_tag} style={{ cursor: errMsg ? "pointer" : "default" }}>
                {labelNode}
              </Tag>
            );
          }

          return errMsg ? <Tooltip title={errMsg}>{tag}</Tooltip> : tag;
        },
      },
      {
        title: "상태 업데이트일시",
        dataIndex: "UPDT_DT",
        key: "UPDT_DT",
        width: 140,
        ellipsis: true,
        render: (text) => (
          <span className={style.table_cell}>{text || "-"}</span>
        ),
      },
      {
        title: "신청기간",
        dataIndex: "PERIOD_NM",
        key: "PERIOD_NM",
        width: 310,
        ellipsis: true,
        render: (text) => (
          <span className={style.table_cell}>{text || "-"}</span>
        ),
      },
    ],
    [attachStatusLabel, getStatusMeta, getLicenseMeta, getRoleMeta],
  );

  const rowSelection = useMemo(
    () => ({
      selectedRowKeys,
      onChange: (keys, rows) => {
        setSelectedRowKeys(keys);
        setSelectedRows(rows);
      },
    }),
    [selectedRowKeys],
  );

  return (
    <div className={style.page_wrap}>
      {/* <SearchArea title="신청 기간 등록"> */}
      <SearchArea title="라이선스 신청 관리">
        <div className={style.searchItemArea} style={{ gap: 50 }}>
          <SearchRow style={{ gap: 20 }}>
            <SearchItem label="신청기간">
              <SearchDropdown
                width={230}
                items={periodList}
                selectedKey={searchPeriod}
                loading={periodLoading}
                onChange={(key, label) => {
                  setSearchPeriod(key);
                }}
              />
            </SearchItem>
            <SearchItem label="상태">
              <SearchDropdown
                width={115}
                items={statusList}
                selectedKey={searchStatus}
                loading={statusLoading}
                onChange={(key, label) => {
                  setSearchStatus(key);
                }}
              />
            </SearchItem>
            <SearchItem label="학과">
              <SearchDropdown
                width={230}
                items={departmentList}
                selectedKey={searchDepartment}
                loading={departmentLoading}
                onChange={(key, label) => {
                  setSearchDepartment(key);
                }}
              />
            </SearchItem>
            <SearchItem label="라이센스">
              <SearchDropdown
                width={230}
                items={licenseList}
                selectedKey={searchLicense}
                loading={licenseLoading}
                onChange={(key, label) => {
                  setSearchLicense(key);
                }}
              />
            </SearchItem>
            <SearchItem label="구분">
              <SearchDropdown
                width={115}
                items={roleList}
                selectedKey={searchRole}
                loading={roleLoading}
                onChange={(key, label) => {
                  setSearchRole(key);
                }}
              />
            </SearchItem>
          </SearchRow>

          <SearchDateRange
            startLabel="신청일 시작"
            endLabel="신청일 종료"
            startDate={startDate}
            endDate={endDate}
            onStartChange={(value) => {
              setStartDate(value);
              // 시작일이 종료일보다 뒤로 가면 종료일을 시작일로 보정(뒤집힘 방지)
              if (value && endDate && value.isAfter(endDate, "day")) {
                setEndDate(value);
              }
            }}
            onEndChange={(value) => {
              setEndDate(value);
              // 종료일이 시작일보다 앞으로 가면 시작일을 종료일로 보정(뒤집힘 방지)
              if (value && startDate && value.isBefore(startDate, "day")) {
                setStartDate(value);
              }
            }}
            startPickerProps={{
              width: 230,
              disabledDate: (current) => {
                if (!endDate || !current) return false;
                return current.isAfter(endDate, "day");
              },
            }}
            endPickerProps={{
              width: 230,
              disabledDate: (current) => {
                if (!startDate || !current) return false;
                return current.isBefore(startDate, "day");
              },
            }}
          />
        </div>
        <div
          style={{ justifyContent: "center", display: "flex", marginRight: 40 }}
        >
          <SearchRow>
            <SearchItem>
              <SearchInput
                placeholder="학번, 이름, 이메일 검색"
                width="520px"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    setCurrentPage(1);
                    setSelectedRowKeys([]);
                    setSelectedRows([]);
                    getApplicantList(1);
                  }
                }}
              />
            </SearchItem>
            <SearchItem>
              <SearchButton onClick={() => {
                    setCurrentPage(1);
                    setSelectedRowKeys([]);
                    setSelectedRows([]);
                    getApplicantList(1);
                  }} disabled={dataLoading}>
                조회
              </SearchButton>
            </SearchItem>
          </SearchRow>
        </div>
      </SearchArea>

      <div className={style.content_wrapper}>
        <div className={style.content_container}>
          <div className={style.header}>
            <div className={style.header_title_group}>
              <div className={style.text}>
                전체 라이선스 신청 (총 {totalCnt}건)
              </div>
              <div className={style.header_left_buttons}>
                <Button
                  className={`${style.sub_action_btn} ${style.sub_action_btn_blue}`}
                  onClick={() => setManualOpen(true)}
                >
                  <PlusIcon width={20} height={20} />
                  사용자 수기 등록
                </Button>
                <Button
                  className={style.sub_action_btn}
                  style={{ marginLeft: 10 }}
                  onClick={() => setExcelUploadOpen(true)}
                >
                  <UploadIcon width={28} height={28} />
                  엑셀 업로드
                </Button>
                <Button
                  className={style.sub_action_btn}
                  loading={downloadLoading}
                  disabled={downloadLoading}
                  onClick={() => {
                    const start =
                      startDate && typeof startDate.format === "function"
                        ? startDate.format("YYYY-MM-DD")
                        : "";
                    const end =
                      endDate && typeof endDate.format === "function"
                        ? endDate.format("YYYY-MM-DD")
                        : "";
                    const params = new URLSearchParams();
                    if (searchStatus && searchStatus !== "ALL")
                      params.append("SEARCH_STATUS", searchStatus);
                    if (searchLicense && searchLicense !== "ALL")
                      params.append("SEARCH_LICENSE", searchLicense);
                    if (searchDepartment && searchDepartment !== "ALL")
                      params.append("SEARCH_DEPARTMENT", searchDepartment);
                    if (searchRole && searchRole !== "ALL")
                      params.append("SEARCH_ROLE", searchRole);
                    if (start) params.append("START_DATE", start);
                    if (end) params.append("END_DATE", end);
                    if (searchText) params.append("SEARCH_TEXT", searchText);

                    setDownloadLoading(true);
                    axios
                      .post(`/application/excel?${params.toString()}`, null, {
                        responseType: "blob",
                      })
                      .then((res) => {
                        const disposition = res.headers["content-disposition"];
                        let filename = "라이선스_신청목록.xlsx";
                        if (disposition) {
                          const match = disposition.match(/filename=(.+)/);
                          if (match && match[1])
                            filename = decodeURIComponent(match[1]);
                        }
                        const url = window.URL.createObjectURL(
                          new Blob([res.data]),
                        );
                        const link = document.createElement("a");
                        link.href = url;
                        link.setAttribute("download", filename);
                        document.body.appendChild(link);
                        link.click();
                        link.remove();
                        window.URL.revokeObjectURL(url);
                      })
                      .catch(() => {
                        message.error("엑셀 다운로드에 실패했습니다.");
                      })
                      .finally(() => {
                        setDownloadLoading(false);
                      });
                  }}
                >
                  {!downloadLoading && (
                    <img
                      src="/img/admin/excel_icon.png"
                      alt=""
                      width={28}
                      height={28}
                    />
                  )}
                  엑셀 다운로드
                </Button>
              </div>
            </div>
            <div className={style.buttons}>
              <Button
                className={style.confirm_btn}
                onClick={() => {
                  if (!selectedRowKeys?.length) {
                    message.warning("선택된 신청건이 없습니다.");
                    return;
                  }
                  // 대기(CD0202) / 에러(CD0203) 상태만 승인 대상
                  const allowed = new Set(["CD0202", "CD0203"]);
                  const targetRows = (selectedRows || []).filter((r) =>
                    allowed.has(String(r?.STATUS || "").trim()),
                  );
                  if (!targetRows.length) {
                    message.warning("승인은 대기 / 에러 상태인 신청건만 가능합니다.");
                    return;
                  }
                  const targetIds = targetRows
                    .map((r) => r?.APPLICATION_ID)
                    .filter(Boolean);
                  const excludedCnt =
                    (selectedRowKeys?.length || 0) - targetIds.length;
                  setApproveNotice(
                    excludedCnt > 0
                      ? `대기/에러 상태가 아닌 ${excludedCnt}건 제외`
                      : "",
                  );
                  setSelectedRows(attachStatusLabel(targetRows));
                  setSelectedRowKeys(targetIds);
                  setApproveTaskType("approve");
                  setApproveOpen(true);
                }}
              >
                <ConfirmAllIcon width={28} height={28} />
                선택 승인
              </Button>
              <Button
                className={style.clear_btn}
                onClick={() => {
                  if (!selectedRowKeys?.length) {
                    message.warning("선택된 신청건이 없습니다.");
                    return;
                  }
                  setSelectedRows(attachStatusLabel(selectedRows));
                  setDeleteOpen(true);
                }}
              >
                <ClearAllIcon width={28} height={28} />
                선택 삭제
              </Button>
            </div>
          </div>
          <div className={style.content}>
            <div className={style.table_section}>
              <Table
                rowKey="APPLICATION_ID"
                rowSelection={{ ...rowSelection }}
                columns={columns.slice(1)}
                dataSource={dataList}
                loading={dataLoading}
                size="middle"
                pagination={{
                  current: currentPage,
                  pageSize: pageSize,
                  total: totalCnt,
                  showSizeChanger: false,
                  onChange: (page) => {
                    setCurrentPage(page);
                    setSelectedRowKeys([]);
                    setSelectedRows([]);
                    getApplicantList(page);
                  },
                }}
                scroll={{ x: 1870 }}
              />
            </div>
          </div>
        </div>
      </div>

      <ApproveModal
        open={approveOpen}
        taskType={approveTaskType}
        rows={selectedRows}
        applicationIds={selectedRowKeys}
        notice={approveNotice}
        onCancel={() => {
          setApproveOpen(false);
          setApproveTaskType("approve");
          setApproveNotice("");
        }}
        onSuccess={(apiResult) => {
          const title = "승인 결과";
          setApproveOpen(false);
          setApproveNotice("");
          openResultWith(title, "", selectedRows, apiResult);
        }}
      />

      <ManualRegisterModal
        open={manualOpen}
        onCancel={() => setManualOpen(false)}
        onSuccess={() => {
          setManualOpen(false);
          getApplicantList(currentPage);
        }}
      />

      <DeleteApplicationModal
        open={deleteOpen}
        rows={selectedRows}
        applicationIds={selectedRowKeys}
        onCancel={() => setDeleteOpen(false)}
        onSuccess={(apiResult) => {
          setDeleteOpen(false);
          openResultWith("삭제 결과", "", selectedRows, apiResult);
        }}
      />

      <ExcelUploadModal
        open={excelUploadOpen}
        onCancel={() => setExcelUploadOpen(false)}
        onSuccess={() => {
          setExcelUploadOpen(false);
          getApplicantList(currentPage);
        }}
      />

      <ResultModal
        open={resultOpen}
        title={resultTitle}
        description={resultDescription}
        summary={resultSummary}
        rows={resultRows}
        onConfirm={() => {
          setResultOpen(false);
          setApproveTaskType("approve");
          setSelectedRowKeys([]);
          setSelectedRows([]);
          getApplicantList(currentPage);
        }}
      />

      <Modal
        title={
          <span className={style.searchItem_label} style={{ fontSize: 18 }}>
            신청 이메일 수정
          </span>
        }
        open={emailModalOpen}
        onCancel={() => setEmailModalOpen(false)}
        closable={false}
        footer={
          <ConfigProvider
            theme={{
              token: { colorPrimary: "#527ff2" },
              components: {
                Button: {
                  colorPrimaryHover: "#4a72de",
                  colorPrimaryActive: "#3f66c8",
                },
              },
            }}
          >
            <div className={style.modal_footer_wrap}>
              <Button
                onClick={() => setEmailModalOpen(false)}
                className={style.modal_footer_btn}
              >
                취소
              </Button>
              <Button
                type="primary"
                className={style.modal_footer_btn}
                onClick={() => {
                  if (!editEmailValue.trim()) {
                    message.warning("이메일을 입력해주세요.");
                    return;
                  }
                  axios
                    .post("/application/email", {
                      applicationId: editEmailAppId,
                      email: editEmailValue.trim(),
                    })
                    .then((res) => {
                      if (res.status === 200) {
                        message.success("이메일이 수정되었습니다.");
                        setEmailModalOpen(false);
                        getApplicantList(currentPage);
                      }
                    })
                    .catch(() => {
                      message.error("이메일 수정에 실패했습니다.");
                    });
                }}
              >
                저장
              </Button>
            </div>
          </ConfigProvider>
        }
        styles={{ mask: { backdropFilter: "none" } }}
        width={480}
        centered
      >
        <div style={{ padding: "23px 0" }}>
          <div className={style.form_row}>
            <div className={style.form_label}>이메일</div>
            <SearchInput
              placeholder="이메일을 입력하세요"
              fullWidth
              value={editEmailValue}
              onChange={(e) => setEditEmailValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  if (!editEmailValue.trim()) {
                    message.warning("이메일을 입력해주세요.");
                    return;
                  }
                  axios
                    .post("/application/email", {
                      applicationId: editEmailAppId,
                      email: editEmailValue.trim(),
                    })
                    .then((res) => {
                      if (res.status === 200) {
                        message.success("이메일이 수정되었습니다.");
                        setEmailModalOpen(false);
                        getApplicantList(currentPage);
                      }
                    })
                    .catch(() => {
                      message.error("이메일 수정에 실패했습니다.");
                    });
                }
              }}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default Application;
